/**
 * Which app prompt a page shows (Figma "London-metro", TASK-1155 designs):
 *
 * - a phone that entered on a route page and got a route → the route
 *   prompt, A or B by experiment, with the destination in variant B;
 * - a phone anywhere else (home, station, line, or a route page whose
 *   route failed) → the one "London is easier with the app" prompt;
 * - a computer → the desktop prompt, 1 or 2 by experiment, with a QR code.
 *
 * The device follows the same user-agent split the links service uses
 * (`storeCtaHref`): iPhone / iPad → App Store, Android → Google Play,
 * everything else is a computer. iPadOS in desktop mode reports a Mac and
 * gets the QR, like any computer.
 */
export type PromptPhone = 'ios' | 'android'

export type PromptSurface =
  | { readonly kind: 'route'; readonly phone: PromptPhone; readonly destination: string }
  | { readonly kind: 'home'; readonly phone: PromptPhone }
  | { readonly kind: 'desktop' }

const IOS = /(iphone|ipad|ipod)/i
const ANDROID = /android/i

export function promptPhone(userAgent: string): PromptPhone | undefined {
  if (IOS.test(userAgent)) return 'ios'
  if (ANDROID.test(userAgent)) return 'android'
  return undefined
}

/** Whether `pathname` is a route page (`/<locale>/routes/<a>-to-<b>` or `/route`). */
export function isRoutePage(pathname: string): boolean {
  const [, kind] = pathname.split('/').filter((segment) => segment !== '')
  return kind === 'routes' || kind === 'route'
}

/**
 * The surface for this visit. `destination` is the route's To station once
 * the route is shown; `undefined` while it is still loading or failed.
 */
export function promptSurface(
  userAgent: string,
  entryPathname: string,
  destination: string | undefined,
): PromptSurface {
  const phone = promptPhone(userAgent)
  if (!phone) return { kind: 'desktop' }
  if (isRoutePage(entryPathname) && destination) return { kind: 'route', phone, destination }
  return { kind: 'home', phone }
}
